import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ApprovalResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetGlobalApprovalsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC approvals data to match the GraphQL query structure
 */
const mapTrpcApprovalsToGraphQL = (
  trpcData: ApprovalResponseRow[]
): GetGlobalApprovalsQuery => {
  return { approval: trpcData };
};

export const useGetGlobalApprovals = (
  isGlobal: boolean,
  parentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.approval.globalApprovals.queryOptions({
      isGlobal,
      parentId,
    }),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcApprovalsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => mapTRPCRefetch(trpcRefetch, mapTrpcApprovalsToGraphQL),
    error: trpcError,
  };
};
